import { axiosClient } from "./axiosClient";

export type GroupMember = {
  id?: string;
  email: string;
  name?: string | null;
  _destroy?: boolean;
};

export type VoterGroup = {
  id: string;
  name: string;
  description?: string | null;
  members_count: number;
  pending_count?: number;
  avatar_url?: string | null;
  member_previews?: { initials: string; avatar_url?: string | null }[];
  group_members?: GroupMember[];
  signup_enabled?: boolean;
  signup_token?: string | null;
  signup_title?: string | null;
  signup_description?: string | null;
  signup_closes_at?: string | null;
  signup_open?: boolean;
  visibility?: "private" | "public";
  requires_approval?: boolean;
  visible_on_profile?: boolean;
  who_can_post?: "all" | "verified" | "owner";
  who_can_create_votings?: "owner" | "moderators" | "members";
  account_required?: boolean;
  verification_required?: boolean;
  content_hidden?: boolean;
  votings?: GroupVoting[];
};

export type VoterGroupPayload = {
  name: string;
  description?: string;
  group_members_attributes?: GroupMember[];
  signup_enabled?: boolean;
  signup_title?: string | null;
  signup_description?: string | null;
  signup_closes_at?: string | null;
  visibility?: "private" | "public";
  requires_approval?: boolean;
  visible_on_profile?: boolean;
  who_can_post?: "all" | "verified" | "owner";
  who_can_create_votings?: "owner" | "moderators" | "members";
  account_required?: boolean;
  verification_required?: boolean;
  content_hidden?: boolean;
};

export type GroupSignupMeta = {
  name: string;
  title?: string | null;
  description?: string | null;
  open: boolean;
  avatar_url?: string | null;
  members_count?: number;
  member_previews?: { initials: string; avatar_url?: string | null }[];
  organizer_name?: string | null;
  account_required?: boolean;
  verification_required?: boolean;
  already_member?: boolean;
  group_id?: string | null;
};

export const getGroupSignupMeta = (token: string) => axiosClient.get<GroupSignupMeta>(`group_signup/${token}`);

export const submitGroupSignup = (token: string, data: { email: string; name?: string; consent: boolean }) =>
  axiosClient.post<{ confirmed?: boolean; confirmation_token?: string }>(`group_signup/${token}`, data);

export const confirmGroupSignup = (confirmation_token: string) =>
  axiosClient.post("group_signup/confirm", { confirmation_token });

export const getVoterGroups = () => axiosClient.get<{ voter_groups: VoterGroup[] }>("voter_groups");

export const getVoterGroup = (id: string) => axiosClient.get<{ voter_group: VoterGroup }>(`voter_groups/${id}`);

export const createVoterGroup = (voter_group: VoterGroupPayload) =>
  axiosClient.post<{ voter_group: VoterGroup }>("voter_groups", { voter_group });

export const updateVoterGroup = (id: string, voter_group: VoterGroupPayload) =>
  axiosClient.put<{ voter_group: VoterGroup }>(`voter_groups/${id}`, { voter_group });

// ── Strona członka („Moje grupy") ──
export type GroupVoting = { id: string; title: string; status: string; start_date?: string | null; end_date?: string | null };

export type MyVoterGroup = {
  id: string;
  name: string;
  description?: string | null;
  avatar_url?: string | null;
  members_count: number;
  organizer_name: string;
  membership_status: "confirmed" | "pending" | "unknown";
  upcoming_votings: GroupVoting[];
  past_votings: GroupVoting[];
};

export const getMyGroups = () => axiosClient.get<{ voter_groups: MyVoterGroup[] }>("user/groups");

export const leaveGroup = (id: string) => axiosClient.delete(`user/groups/${id}/leave`);

export const deleteVoterGroup = (id: string) => axiosClient.delete(`voter_groups/${id}`);

// ── Skalowalne zarządzanie członkami (paginacja + zakładki statusu + operacje per-członek) ──
export type GroupMemberStatus = "active" | "pending" | "blocked" | "account" | "email" | "all";

export type GroupMemberDetail = {
  id: string;
  email: string;
  name?: string | null;
  source: string;
  confirmed: boolean;
  moderator?: boolean;
  blocked?: boolean;
  is_owner?: boolean;
  is_me?: boolean; // wiersz zalogowanego moderatora — bez akcji moderacyjnych na sobie
  has_account?: boolean; // czy e-mail koreluje z istniejącym kontem (osobny „worek" od zwykłych e-maili)
  avatar_url?: string | null; // ścieżka awataru konta skorelowanego z e-mailem (lub null)
  profile_ref?: string | null; // ref publicznego profilu skorelowanego konta (deep-link) lub null
};

export type GroupMembersMeta = {
  page: number;
  per_page: number;
  total_pages: number;
  total_count: number;
  active_count: number;
  pending_count: number;
  blocked_count?: number;
  account_count?: number;
  email_only_count?: number;
  can_appoint?: boolean;
};

export type GroupMembersResponse = { members: GroupMemberDetail[]; meta: GroupMembersMeta };

export const getGroupMembers = (
  groupId: string,
  opts: { status?: GroupMemberStatus; q?: string; page?: number } = {}
) =>
  axiosClient.get<GroupMembersResponse>(`voter_groups/${groupId}/members`, {
    params: { status: opts.status, q: opts.q || undefined, page: opts.page || 1 }
  });

export const addGroupMembers = (groupId: string, members: { email: string; name?: string | null }[]) =>
  axiosClient.post<{ added: number; blocked?: number; meta: Pick<GroupMembersMeta, "total_count" | "active_count" | "pending_count"> }>(
    `voter_groups/${groupId}/members`,
    { members }
  );

// Podpowiadanie kont do dodania: pełny e-mail → dokładnie; fraza → publiczne profile (tylko zezwalające na dodawanie).
export type MemberLookupResult = {
  email: string;
  name: string;
  avatar_url?: string | null;
  profile_ref?: string | null;
  already_member: boolean;
};

export const getMemberLookup = (groupId: string, q: string) =>
  axiosClient.get<{ results: MemberLookupResult[] }>(`voter_groups/${groupId}/members/lookup`, { params: { q } });

// Smart-search kont do dodania jako uprawnieni (bez kontekstu grupy) — prywatność jak w members/lookup.
export type VoterLookupResult = Omit<MemberLookupResult, "already_member">;
export const getUsersLookup = (q: string) =>
  axiosClient.get<{ results: VoterLookupResult[] }>("users/lookup", { params: { q } });

export const updateGroupMember = (groupId: string, memberId: string, name: string | null) =>
  axiosClient.put<GroupMemberDetail>(`voter_groups/${groupId}/members/${memberId}`, { member: { name } });

export const deleteGroupMember = (groupId: string, memberId: string) =>
  axiosClient.delete(`voter_groups/${groupId}/members/${memberId}`);

// ── Zarządzanie członkami (topbar w widoku grupy): blokowanie / mianowanie moderatorów ──
export const blockGroupMember = (groupId: string, memberId: string) =>
  axiosClient.post<GroupMemberDetail>(`voter_groups/${groupId}/members/${memberId}/block`);

export const unblockGroupMember = (groupId: string, memberId: string) =>
  axiosClient.post<GroupMemberDetail>(`voter_groups/${groupId}/members/${memberId}/unblock`);

export const approveGroupMember = (groupId: string, memberId: string) =>
  axiosClient.post<GroupMemberDetail>(`voter_groups/${groupId}/members/${memberId}/approve`);

export const promoteGroupMember = (groupId: string, memberId: string) =>
  axiosClient.post<GroupMemberDetail>(`voter_groups/${groupId}/members/${memberId}/promote`);

export const demoteGroupMember = (groupId: string, memberId: string) =>
  axiosClient.post<GroupMemberDetail>(`voter_groups/${groupId}/members/${memberId}/demote`);

export const importVoterGroupCsv = (id: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  return axiosClient.post<{ voter_group: VoterGroup }>(`voter_groups/${id}/import`, formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });
};

export const uploadVoterGroupAvatar = (id: string, file: File) => {
  const formData = new FormData();
  formData.append("avatar", file);
  return axiosClient.post<{ voter_group: VoterGroup }>(`voter_groups/${id}/avatar`, formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });
};

export const removeVoterGroupAvatar = (id: string) =>
  axiosClient.delete<{ voter_group: VoterGroup }>(`voter_groups/${id}/avatar`);
